import {
  SearchTeamsByFieldsPayload,
  TeamSearchSortBy,
  TeamSearchSortOrder,
} from 'TeamModule/api/searchTeamsByFieldsApi';

// Filter fields forwarded from the shared Teams Management filters onto the
// pending-deletion search. `text` powers the top search bar (GLX-1808 — it was
// previously dropped here, so searching did nothing on the deletion tab).
const STRING_FIELDS = ['tenant', 'realmId', 'teamId', 'csm', 'text'] as const;

const ARRAY_FIELDS = ['teamIds', 'teamIdsNot', 'tags', 'integrations'] as const;

const BOOLEAN_FIELDS = [
  'ssoEnabled',
  'openApiEnabled',
  'ddiEnabled',
  'internalTestTeam',
  'chelloCompliant',
  'aiEnabled',
  'aiBlocked',
  'personal',
  'discarded',
  'deactivationScheduled',
] as const;

export const buildPendingDeletionSearchParams = ({
  filtersPayload,
  sortBy,
  sortOrder,
  page,
  limit,
}: {
  filtersPayload: SearchTeamsByFieldsPayload;
  sortBy: TeamSearchSortBy | undefined;
  sortOrder: TeamSearchSortOrder;
  page: number;
  limit: number;
}): SearchTeamsByFieldsPayload => {
  // deletionScheduled/dataDeleted pin the result set to teams awaiting
  // deletion; they are not overridable by the shared filters.
  const params: SearchTeamsByFieldsPayload = {
    deletionScheduled: true,
    dataDeleted: false,
    sortBy,
    sortOrder,
    page,
    limit,
  };

  for (const field of STRING_FIELDS) {
    const value = filtersPayload[field];
    if (value) {
      params[field] = value;
    }
  }

  for (const field of ARRAY_FIELDS) {
    const value = filtersPayload[field];
    if (Array.isArray(value) && value.length > 0) {
      params[field] = value;
    }
  }

  for (const field of BOOLEAN_FIELDS) {
    const value = filtersPayload[field];
    if (typeof value === 'boolean') {
      params[field] = value;
    }
  }

  return params;
};

export type AiSuggestionsFilter = 'all' | 'on' | 'off' | 'blocked';

export const AI_SUGGESTIONS_FILTER_OPTIONS: Array<{ value: AiSuggestionsFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
  { value: 'blocked', label: 'Blocked' },
];

/** The search filters the Teams page AI Suggestions filter stands for. */
export const aiSuggestionsFilterToPayload = (
  filter: AiSuggestionsFilter,
): Pick<SearchTeamsByFieldsPayload, 'aiEnabled' | 'aiBlocked'> => {
  // A blocked team reads as Blocked even before its turn-off reaches the list.
  if (filter === 'on') return { aiEnabled: true, aiBlocked: false };
  // A blocked team is also off; Off lists the ones a CSM can turn on.
  if (filter === 'off') return { aiEnabled: false, aiBlocked: false };
  if (filter === 'blocked') return { aiBlocked: true };
  return {};
};
